import React, { useState, useEffect } from 'react';
import Map, { Marker, NavigationControl, GeolocateControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

interface CityMapViewProps {
  city: string;
  properties: Array<{
    mlsNumber: string;
    address: string;
    price: number;
    coordinates?: {
      longitude: number;
      latitude: number;
    };
  }>;
  onPropertyClick?: (mlsNumber: string) => void;
}

// City center coordinates
const cityCoordinates: { [key: string]: { lat: number; lng: number } } = {
  'gilbert': { lat: 33.3528, lng: -111.7890 },
  'scottsdale': { lat: 33.4942, lng: -111.9261 },
  'phoenix': { lat: 33.4484, lng: -112.0740 },
  'mesa': { lat: 33.4152, lng: -111.8315 },
  'chandler': { lat: 33.3062, lng: -111.8413 },
  'tempe': { lat: 33.4255, lng: -111.9400 },
  'glendale': { lat: 33.5387, lng: -112.1860 },
  'peoria': { lat: 33.5806, lng: -112.2374 },
  'surprise': { lat: 33.6292, lng: -112.3679 },
  'avondale': { lat: 33.4356, lng: -112.3496 }
};

export const CityMapView: React.FC<CityMapViewProps> = ({ city, properties, onPropertyClick }) => {
  const cityLower = city.toLowerCase();
  const center = cityCoordinates[cityLower] || { lat: 33.4484, lng: -112.0740 }; // Default to Phoenix
  
  const [viewState, setViewState] = useState({
    longitude: center.lng,
    latitude: center.lat,
    zoom: 11,
    pitch: 0,
    bearing: 0
  });

  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);

  // Filter properties with valid coordinates
  const validProperties = properties.filter(p => 
    p.coordinates && 
    !isNaN(p.coordinates.longitude) && 
    !isNaN(p.coordinates.latitude)
  );

  return (
    <div className="w-full h-full relative">
      <Map
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        maxTileCacheSize={50}
        reuseMaps
      >
        <NavigationControl position="top-right" />
        <GeolocateControl position="top-right" />

        {/* Property markers */}
        {validProperties.map((property) => (
          <Marker
            key={property.mlsNumber}
            longitude={property.coordinates!.longitude}
            latitude={property.coordinates!.latitude}
            anchor="bottom"
          >
            <div
              className={`cursor-pointer transition-all transform hover:scale-110 ${
                selectedProperty === property.mlsNumber ? 'scale-110' : ''
              }`}
              onClick={() => {
                setSelectedProperty(property.mlsNumber);
                onPropertyClick?.(property.mlsNumber);
              }}
            >
              <div className="relative">
                <svg
                  width="40"
                  height="48"
                  viewBox="0 0 40 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 0C8.955 0 0 8.955 0 20C0 35 20 48 20 48S40 35 40 20C40 8.955 31.045 0 20 0Z"
                    fill={selectedProperty === property.mlsNumber ? "#EF4444" : "#2563EB"}
                  />
                  <circle cx="20" cy="20" r="15" fill="white" />
                </svg>
                <div className="absolute inset-0 flex items-start justify-center pt-2">
                  <span className="text-xs font-bold text-blue-600">
                    ${Math.round(property.price / 1000)}K
                  </span>
                </div>
              </div>
            </div>
          </Marker>
        ))}
      </Map>

      {/* Property count overlay */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg px-4 py-2">
        <p className="text-sm font-semibold">
          {validProperties.length} properties shown
        </p>
      </div>
    </div>
  );
};